<template>
  <div>
    <div
      v-if="!showTeamMemberList"
      class="relative pb-2"
    >
      <section class="share__presentation-online-link">
        <p class="share__section-description" v-if="!hideTitle">
          {{ $i18nPresenter.t(`To let others edit this presentation with you,` +
            `please enter their email addresses here:`) }}
        </p>
        <form
          class="flex items-start mb-2"
          @submit.prevent="onSubmit()"
        >
          <div class="mr-2 share-input-tag-wrp">
            <AhaInputTag
              id="input-tag__share-to-edit"
              ref="collaborators-input"
              class="overflow-y-auto max-h-24 pr-8"
              v-model="emails"
              :allow-duplicate="false"
              :placeholder="emails.length > 0 ? '' : inputTagPlaceHolder"
              :validator="emailRegex"
              :formatter="(entry) => entry.toLowerCase()"
            />
          </div>

          <vue-recaptcha
            :sitekey="keyRecaptcha"
            @verify="onVerify"
            @error="onRecaptchaError"
            size="invisible"
            @expired="onExpired"
            :load-recaptcha-script="true"
            recaptcha-host="www.recaptcha.net"
            ref="recaptcha"
            class="auth-captcha"
          />

          <aha-button
            type="submit"
            class="py-2"
            ref="shareToEditButton"
          >
            <span v-if="!submitting">{{ $i18nPresenter.t('Invite') }}</span>
            <aha-spinner
              class="ml-2"
              v-if="submitting"
              :variant="'white'"
            />
          </aha-button>
        </form>
      </section>

      <div v-if="sendACopyVisible" class="mb-1">
        <section>
          <a @click="handleShowShareToEditList()" class="mb-2 font-bold text-link--primary">
            {{ $i18nPresenter.t('Select team members...') }}
          </a>
        </section>
      </div>
      <PresentationCollaborateList
        :presentation="presentation"
        :collaborators="collaborators"
        :isDisableEditPresentation="isDisableEditPresentation"
        @removeCollaborator="removeCollaborator"
        :loadingCollaborators="loadingCollaborators"
      />
    </div>
  </div>
</template>

<script>
import VueRecaptcha from 'vue-recaptcha'

import { mapState } from 'vuex'
import configs from '@/config'

import Utils from '@/util'
import PresentationApi from '@/api/presentation'
import PresentationCollaborateList from '@/components-v2/presentation-editor/collaboration/PresentationCollaborateList.vue'
import constants from '@/constant'
import EventBus from '@/eventBus'

const { eventCategories } = constants
// components

export default {
  components: {
    PresentationCollaborateList,
    VueRecaptcha,
  },
  props: {
    hideTitle: {
      type: Boolean,
      default: false,
    },
    presentation: {
      type: Object,
      required: false,
      default() {
        return null
      },
    },
    hasPadding: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      keyRecaptcha: configs.keyRecapcha,
      tokenRecaptcha: '',
      submitting: false,
      textError: '',
      showTeamMemberList: false,
      emails: [],
      collaborators: [],
      loadingCollaborators: false,
      isSendingInvitations: false,
      emailRegex: "^(([^<>()[\\]\\.,;:\\s@']+(\\.[^<>()[\\]\\.,;:\\s@']+)*)|('.+'))@((\\[[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\])|(([a-zA-Z\\-0-9]+\\.)+[a-zA-Z]{2,}))$",

    }
  },
  computed: {
    ...mapState({
      currentUser: (state) => state.currentUser,
      currentAccount: (state) => state.currentAccount,
      isDisableEditPresentation: (state) => state.isDisableEditPresentation,
    }),
    hasTeam() {
      return this.currentAccount && this.currentAccount.id
    },
    isNotSkoletube() {
      return this.currentUser.identifyProvider !== 'skoletube'
    },
    sendACopyVisible() {
      return this.hasTeam && this.isNotSkoletube
    },
    inputTagPlaceHolder() {
      return this.$i18nPresenter.t('Enter emails, comma separated')
    },
  },
  mounted() {
    setTimeout(() => {
      this.focusInviteeInput()
    }, 150)
    this.getCollaboratorList(this.presentation.id)
  },
  methods: {
    onVerify(response) {
      this.tokenRecaptcha = response
      this.shareToEdit()
    },
    onRecaptchaError() {
      this.submitting = false
      this.textError = this.$i18nPresenter.t('Recaptcha error. If the problem still occurs, please check your internet connection.')
      this.$store.dispatch('setSyncPresentation', 3)
    },
    onSubmit() {
      if (this.submitting) return
      this.submitting = true
      if (this.currentUser.email.match(/qa\.ahaslides\+(.*)@gmail.com/i)) {
        this.shareToEdit()
      } else {
        this.$refs.recaptcha.execute()
        Utils.removeRecaptchaCover()
      }
    },
    onExpired() {
      this.$refs.recaptcha.reset()
      this.tokenRecaptcha = ''
    },
    focusInviteeInput() {
      const input = document.getElementById('input-tag__share-to-edit').children[0]
      if (!input) return
      input.focus()
    },
    getOwner(presentation) {
      return {
        ...presentation.User,
        role: 'owner',
      }
    },
    async getCollaboratorList(presentationId) {
      this.loadingCollaborators = true
      const res = await PresentationApi.getCollaborators(presentationId)
      this.collaborators = res.invitedUsers.map((collaborator) => {
        return {
          ...collaborator,
          role: 'editor',
        }
      })
      this.collaborators = [this.getOwner(this.presentation)].concat(this.collaborators)
      this.loadingCollaborators = false
    },
    async shareToEdit() {
      if (!this.isInputTagHasValue && this.emails.length === 0) {
        Utils.showToastError(this.$i18nPresenter.t('Please enter member\'s email'))
        this.focusInviteeInput()
        this.$refs.recaptcha.reset()
        this.tokenRecaptcha = ''
        this.submitting = false
        return
      }
      const { email: ownerEmail } = this.getOwner(this.presentation)
      if (this.emails.includes(ownerEmail)) {
        Utils.showToastError(this.$i18nPresenter.t('Cannot share this presentation to its owner'))
        this.$refs.recaptcha.reset()
        this.tokenRecaptcha = ''
        this.submitting = false
        return
      }
      if (this.emails.some((email) => !Utils.validateEmail(email))) {
        Utils.showToastError(this.$i18nPresenter.t('Some of the emails are invalid'))
        this.$refs.recaptcha.reset()
        this.tokenRecaptcha = ''
        this.submitting = false
        return
      }
      if (this.emails.includes(this.currentUser.email)) {
        Utils.showToastError(this.$i18nPresenter.t('Cannot share this presentation to yourself'))
        this.$refs.recaptcha.reset()
        this.tokenRecaptcha = ''
        this.submitting = false
        return
      }
      let gaCategoryType = eventCategories.PRESENTATION_EDITOR
      if (this.$route.name === 'Presentations' || this.$route.name === 'FolderDetail') {
        gaCategoryType = eventCategories.PRESENTATION_LIST
      }
      Utils.mixpanelTracking(gaCategoryType, 'Presentation > Invite to edit > Submit', this.emails.join(','), null, {
        plan: this.currentUser.plan,
        presentationId: this.presentation.id,
        userId: this.currentUser.id,
        isPresentationOwner: this.presentation.userId === this.currentUser.id,
      })

      this.submitting = true
      // Because we have 2 elements listen to the Enter key at the same time (the form & the input tag),
      // setTimeout a bit for waiting the input tag (emails) to be added after Enter pressed
      setTimeout(async () => {
        const button = this.$refs.shareToEditButton
        // focus button & blur input so the tag input does not expand
        // this is because the input has bigger width when focused
        if (button) button.$el.focus()
        try {
          await PresentationApi.addCollaborators(this.presentation.id, {
            collaborators: this.emails,
            tokenRecaptcha: this.tokenRecaptcha,
          })

          await this.getCollaboratorList(this.presentation.id)
          if (!this.hasTeam) {
            // for the first time, invite all the existing collaborators
            this.$emit('prepareDataForInviteTeamModal', {
              toBeInvitedMembers: this.collaborators,
            })
          } else {
            // for subsequent times, invite only the newly added collaborators
            this.$emit('prepareDataForInviteTeamModal', {
              toBeInvitedMembers: this.collaborators.filter((i) => this.emails.includes(i.email)),
            })
          }
          Utils.mixpanelTracking(gaCategoryType, 'Presentation > Invite to edit > Succeeded', this.emails.join(','), null, {
            plan: this.currentUser.plan,
            presentationId: this.presentation.id,
            userId: this.currentUser.id,
            isPresentationOwner: this.presentation.userId === this.currentUser.id,
          })
          Utils.showToastSuccess(`${this.$i18nPresenter.t('Successfully invited')} ${this.emails.length} ${this.$i18nPresenter.t('member(s) to edit this presentation.')}`)

          this.$refs['collaborators-input'].clearAllTags()
        } catch (e) {
          Utils.mixpanelTracking(gaCategoryType, 'Presentation > Invite to edit > Failed')
          if (e.body && e.body.message) {
            Utils.showToastError(e.body.message)
          } else {
            Utils.showToastError(`${this.$i18nPresenter.t('Failed to invite')} ${this.emails.length} ${this.$i18nPresenter.t('member(s) to edit this presentation.')}`)
          }
        } finally {
          this.emails = []
          this.submitting = false
          this.$refs.recaptcha.reset()
          this.tokenRecaptcha = ''
        }
      }, 1)
    },

    async removeCollaborator(collaboratorToDelete) {
      let gaCategoryType = eventCategories.PRESENTATION_EDITOR
      if (this.$route.name === 'Presentations' || this.$route.name === 'FolderDetail') {
        gaCategoryType = eventCategories.PRESENTATION_LIST
      }
      try {
        await PresentationApi.removeCollaborators(this.presentation.id, {
          collaborators: [collaboratorToDelete.email],
        })
        this.collaborators = this.collaborators
          .filter((collaborator) => collaborator.email !== collaboratorToDelete.email)

        Utils.mixpanelTracking(gaCategoryType, 'Presentation > Remove collaborator > Succeeded')
        if (collaboratorToDelete.email !== this.currentUser.email) {
          Utils.showToastSuccess(`${this.$i18nPresenter.t('Successfully remove')} ${collaboratorToDelete.email} ${this.$i18nPresenter.t('from editing this presentation.')}`)
        } else {
          Utils.showToastSuccess(this.$i18nPresenter.t('Left this presentation successfully.'))
        }

        // If the user remove himselft from collaborators list -> Redirect him to Presentation List
        if (collaboratorToDelete.email === this.currentUser.email) {
          if (this.$route.name === 'PresentationDetail') {
            this.$router.replace({
              name: 'Presentations',
            })
          } else {
            EventBus.$emit('reload-presentations-and-folders')
          }
        }
      } catch (e) {
        Utils.mixpanelTracking(gaCategoryType, 'Presentation > Remove collaborator > Succeeded')
        Utils.showToastError(`${this.$i18nPresenter.t('Fail to remove')} ${collaboratorToDelete.email} ${this.$i18nPresenter.t('from editing this presentation.')}`)
      }
    },

    handleBackButtonClick() {
      this.showTeamMemberList = false
      this.$emit('handle-hide-main-header')
    },

    handleShowShareToEditList() {
      this.$emit('handleShowShareToEditList')
    },

  },
}
</script>

<style lang="scss" scoped>

.share__section-title {
  @apply mb-3 text-sm font-bold text-secondary-100;
}

.share-input-tag-wrp {
  width: 90%;
  position: relative;
}

.share__section-description {
  @apply mb-3 text-xs;
}
.share__presentation-online-link {
  @apply mb-3;
}
@media (max-width: 580px) {
}

</style>
